import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useAppStore } from '../stores/app'
import { useBrandStore } from '../stores/brand'
import { useGuestStore } from '../stores/guest'
import { useBranding } from '../composables/useBranding'
import { useShopBrand } from '../composables/useShopBrand'
import { useShopDomain } from '../composables/useShopDomain'
import { googleAnalytics } from '../services/googleAnalytics'
import type { RouteMeta } from '../types/auth'

/**
 * Rewrite a `/shop/:shopCode/<tail>` path into its clean-URL equivalent so
 * a WL custom domain visitor never sees the shopCode in their URL bar. The
 * shopCode is dropped (the host already implies the shop) and the tail is
 * mapped to the root-level clean route.
 *
 * Returns null when the path doesn't match `/shop/:shopCode/*`, OR when the
 * first segment after `/shop/` is a known *global* helper route (not a
 * shopCode) — those need to stay reachable on every host.
 *
 * Examples:
 *   /shop/abc                       → /
 *   /shop/abc/products              → /products
 *   /shop/abc/product/widget        → /product/widget
 *   /shop/abc/cart                  → /cart
 *   /shop/abc/checkout              → /checkout
 *   /shop/abc/about                 → /about
 *   /shop/abc/orders/ABC-123        → /orders/ABC-123
 *   /shop/order/lookup              → /track-order  (special-cased)
 *   /shop/orders/live               → null          (admin route, leave alone)
 */
function rewriteShopPathToClean(path: string): string | null {
  // Specials: `/shop/order/lookup` is a marketplace-wide helper route, not
  // a per-shop route. On WL we route it to the clean `/track-order` alias.
  if (path === '/shop/order/lookup' || path.startsWith('/shop/order/lookup?')) {
    return '/track-order'
  }
  // Reserved first segments under /shop/ that are NOT shopCodes — leave them
  // alone so they keep resolving to their existing routes.
  const reservedFirstSegments = ['order', 'orders']
  const reservedMatch = /^\/shop\/([^/]+)/.exec(path)
  if (reservedMatch && reservedFirstSegments.includes(reservedMatch[1])) {
    return null
  }
  // Generic path shape: /shop/<code>(/<rest>)?
  const match = /^\/shop\/[^/]+(\/.*)?$/.exec(path)
  if (!match) return null
  const tail = match[1] ?? ''
  return tail === '' || tail === '/' ? '/' : tail
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to: any, _from: any, savedPosition: { left: number; top: number } | null) {
    // If the user navigates back/forward, restore their saved scroll position
    if (savedPosition) {
      return savedPosition
    }

    // If there's a hash (anchor link), scroll to that element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }

    // For all other navigation, scroll to top of page
    // Using a small delay ensures the new page content is rendered before scrolling
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ top: 0, left: 0, behavior: 'instant' })
      }, 0)
    })
  },
  routes: [
    // Brochure page
    {
      path: '/brochure',
      name: 'Brochure',
      component: () => import('../views/public/BrochureView.vue'),
      meta: {
        title: 'Marketing Brochure',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    // Public routes - Brand-specific home pages
    // The `/` entry uses a wrapper component (RootEntryView) so a WL custom
    // domain renders its own storefront here without the URL ever leaving
    // `/`. Canonical Zaveato visitors get the marketplace HomeView as before.
    {
      path: '/',
      name: 'Home',
      component: () => import('../views/RootEntryView.vue'),
      meta: {
        title: 'Home',
        requiresAuth: false  // Explicitly mark as public route
        // hideNavigation is false by default, allowing navigation to show
      } as RouteMeta
    },
    {
      path: '/pricing',
      name: 'Pricing',
      component: () => import('../views/public/PricingView.vue'),
      meta: {
        title: 'Pricing Plans',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/how-it-works',
      name: 'HowItWorks',
      component: () => import('../views/public/HowItWorksView.vue'),
      meta: {
        title: 'How It Works',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/about',
      name: 'About',
      component: () => import('../views/AboutEntryView.vue'),
      meta: {
        title: 'About Us',
        requiresAuth: false
      } as RouteMeta
    },

    // Backward-compat redirects — Google has old cached URLs for these routes
    // from an earlier version of the site. Rather than let the SPA fall
    // through to homepage (soft-404), 301 them to their canonical equivalents
    // so link equity from those old indexed URLs flows to the current pages.
    // Safe to remove after Google fully re-crawls (~3-6 months post-launch).
    { path: '/about-us',        redirect: '/about' },
    { path: '/browse-shops',    redirect: '/shops' },
    { path: '/browse-services', redirect: '/services' },

    // Setup Guide routes (public)
    {
      path: '/guide/service-setup',
      name: 'ServiceSetupGuide',
      component: () => import('../views/public/ServiceSetupGuideView.vue'),
      meta: {
        title: 'How to Setup Your Service',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/guide/shop-setup',
      name: 'ShopSetupGuide',
      component: () => import('../views/public/ShopSetupGuideView.vue'),
      meta: {
        title: 'How to Setup Your Shop',
        requiresAuth: false
      } as RouteMeta
    },

    // Authentication routes
    {
      path: '/auth',
      component: () => import('../views/auth/AuthLayout.vue'),
      meta: { 
        requiresGuest: true,
        hideNavigation: true 
      } as RouteMeta,
      children: [
        {
          path: '',
          redirect: '/auth/login'
        },
        {
          path: 'login',
          name: 'Login',
          component: () => import('../views/auth/LoginView.vue'),
          meta: { 
            title: 'Login',
            requiresGuest: true 
          } as RouteMeta
        },
        {
          path: 'forgot-password',
          name: 'ForgotPassword',
          component: () => import('../views/auth/ForgotPasswordView.vue'),
          meta: { 
            title: 'Forgot Password',
            requiresGuest: true 
          } as RouteMeta
        },
        {
          path: 'reset-password',
          name: 'ResetPassword',
          component: () => import('../views/auth/ResetPasswordView.vue'),
          meta: {
            title: 'Reset Password',
            requiresGuest: true
          } as RouteMeta
        }
      ]
    },

    // Customer registration route (standalone with its own layout)
    {
      path: '/auth/register',
      name: 'Register',
      component: () => import('../views/auth/RegisterView.vue'),
      meta: {
        title: 'Register',
        requiresGuest: true,
        hideNavigation: true
      } as RouteMeta
    },

    // Apple Sign In callback route (standalone, no auth layout)
    {
      path: '/auth/callback/apple',
      name: 'AppleCallback',
      component: () => import('../views/auth/AppleCallbackView.vue'),
      meta: {
        title: 'Apple Sign In',
        requiresGuest: true,
        hideNavigation: true
      } as RouteMeta
    },

    // Partner signup route (standalone with its own layout)
    {
      path: '/partner/signup',
      name: 'PartnerSignup',
      component: () => import('../views/auth/PartnerSignupView.vue'),
      meta: {
        title: 'Partner Signup',
        requiresGuest: true,
        hideNavigation: true
      } as RouteMeta
    },

    // Email verification route (outside auth layout to allow access after registration)
    {
      path: '/verify-email',
      name: 'VerifyEmail',
      component: () => import('../views/auth/VerifyEmailView.vue'),
      meta: {
        title: 'Verify Email',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },

    // Email verification required page (shown when user tries to login without verifying)
    {
      path: '/email-verification',
      name: 'EmailVerificationRequired',
      component: () => import('../views/EmailVerificationView.vue'),
      meta: {
        title: 'Email Verification Required',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },

    // Protected routes (for regular clients, not providers)
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: {
        requiresAuth: true,
        title: 'Dashboard'
      } as RouteMeta,
      beforeEnter: (_to: any, _from: any, next: any) => {
        const authStore = useAuthStore()

        // Redirect providers to Provider Hub
        if (authStore.hasRole('Provider')) {
          next({ path: '/provider' })
        } else if (authStore.hasRole('ServiceUser')) {
          next({ path: '/service-user/dashboard' })
        } else {
          next()
        }
      }
    },

    // Customer orders (Zaveato Shop)
    {
      path: '/my-orders',
      name: 'MyOrders',
      component: () => import('../views/customer/CustomerOrdersView.vue'),
      meta: {
        requiresAuth: true,
        title: 'My Orders'
      } as RouteMeta
    },
    
    // Profile routes
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('../views/ProfileView.vue'),
      meta: { 
        requiresAuth: true,
        title: 'Profile' 
      } as RouteMeta
    },
    {
      path: '/profile/edit',
      name: 'ProfileEdit',
      component: () => import('../views/profile/ProfileEditView.vue'),
      meta: { 
        requiresAuth: true,
        title: 'Edit Profile' 
      } as RouteMeta
    },
    {
      path: '/profile/security',
      name: 'ProfileSecurity',
      component: () => import('../views/profile/SecurityView.vue'),
      meta: { 
        requiresAuth: true,
        title: 'Security Settings' 
      } as RouteMeta
    },
    {
      path: '/profile/preferences',
      name: 'ProfilePreferences',
      component: () => import('../views/profile/PreferencesView.vue'),
      meta: { 
        requiresAuth: true,
        title: 'Preferences' 
      } as RouteMeta
    },
    {
      path: '/profile/activity',
      name: 'ProfileActivity',
      component: () => import('../views/profile/ActivityView.vue'),
      meta: {
        requiresAuth: true,
        title: 'Activity'
      } as RouteMeta
    },
    {
      path: '/profile/addresses',
      name: 'ProfileAddresses',
      component: () => import('../views/profile/ProfileAddressView.vue'),
      meta: {
        requiresAuth: true,
        title: 'My Addresses'
      } as RouteMeta
    },

    // Admin routes (Unified Admin - Services and Shop)
    {
      path: '/admin',
      component: () => import('../views/admin/UnifiedAdminLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Admin'],
        hideNavigation: true
      } as RouteMeta,
      children: [
        {
          path: '',
          redirect: '/admin/reports'
        },
        {
          path: 'profile',
          name: 'AdminProfile',
          component: () => import('../views/admin/AdminProfileView.vue'),
          meta: {
            title: 'Admin Profile'
          } as RouteMeta
        },
        {
          path: 'users',
          name: 'AdminUsers',
          component: () => import('../views/admin/UsersView.vue'),
          meta: {
            title: 'User Management',
            permissions: ['users.viewAll']
          } as RouteMeta
        },
        {
          path: 'users/:id',
          name: 'AdminUserDetail',
          component: () => import('../views/admin/UserDetailView.vue'),
          meta: {
            title: 'User Details',
            permissions: ['users.viewAll']
          } as RouteMeta
        },
        {
          path: 'providers',
          name: 'AdminProviders',
          component: () => import('../views/admin/ProvidersView.vue'),
          meta: {
            title: 'Providers Management',
            permissions: ['providers.viewAll']
          } as RouteMeta
        },
        {
          path: 'providers/:id',
          name: 'AdminProviderDetail',
          component: () => import('../views/provider/ProfileView.vue'),
          meta: {
            title: 'Provider Details',
            permissions: ['providers.viewAll']
          } as RouteMeta
        },
        {
          path: 'clients',
          name: 'AdminClients',
          component: () => import('../views/admin/ClientsView.vue'),
          meta: {
            title: 'Clients Management'
          } as RouteMeta
        },
        {
          path: 'clients/:id',
          name: 'AdminClientDetail',
          component: () => import('../views/admin/UserDetailView.vue'),
          meta: {
            title: 'Client Details',
            permissions: ['users.viewAll']
          } as RouteMeta
        },
        {
          path: 'services',
          name: 'AdminServices',
          component: () => import('../views/admin/ServicesView.vue'),
          meta: {
            title: 'Services Management',
            permissions: ['services.viewAll']
          } as RouteMeta
        },
        {
          path: 'service-categories',
          name: 'AdminServiceCategories',
          component: () => import('../views/admin/ServiceCategoriesView.vue'),
          meta: {
            title: 'Service Categories Management',
            permissions: ['services.viewAll']
          } as RouteMeta
        },
        {
          path: 'specializations',
          name: 'AdminSpecializations',
          component: () => import('../views/admin/SpecializationsView.vue'),
          meta: {
            title: 'Specializations Management',
            permissions: ['providers.viewAll']
          } as RouteMeta
        },
        {
          path: 'appointments',
          name: 'AdminAppointments',
          component: () => import('../views/admin/AppointmentsView.vue'),
          meta: {
            title: 'Appointments Management',
            permissions: ['appointments.viewAll']
          } as RouteMeta
        },
        {
          path: 'appointments/:id',
          name: 'AdminAppointmentDetail',
          component: () => import('../views/appointments/AppointmentDetailsView.vue'),
          meta: {
            title: 'Appointment Details',
            permissions: ['appointments.viewAll']
          } as RouteMeta
        },
        {
          path: 'services/commissions',
          name: 'AdminServiceCommissions',
          component: () => import('../views/admin/ServiceCommissionsView.vue'),
          meta: {
            title: 'Services Commission Rates'
          } as RouteMeta
        },
        {
          path: 'settings',
          name: 'AdminSettings',
          component: () => import('../views/admin/SettingsView.vue'),
          meta: {
            title: 'System Settings',
            permissions: ['settings.view']
          } as RouteMeta
        },
        {
          path: 'utilities',
          name: 'AdminUtilities',
          component: () => import('../views/admin/UtilitiesView.vue'),
          meta: {
            title: 'Utilities',
            permissions: ['settings.view']
          } as RouteMeta
        },
        // Financial Section
        {
          path: 'commission-reports',
          name: 'AdminCommissionReports',
          component: () => import('../views/admin/CommissionReportsView.vue'),
          meta: { title: 'Commission Reports' } as RouteMeta
        },
        {
          path: 'commission-invoices',
          name: 'AdminCommissionInvoices',
          component: () => import('../views/admin/CommissionInvoicesView.vue'),
          meta: { title: 'Commission Invoices' } as RouteMeta
        },
        {
          path: 'payouts',
          name: 'AdminPayouts',
          component: () => import('../views/admin/PayoutsView.vue'),
          meta: { title: 'Payouts' } as RouteMeta
        },
        {
          path: 'reports',
          name: 'AdminReports',
          component: () => import('../views/admin/ReportsView.vue'),
          meta: {
            title: 'Reports',
            permissions: ['reports.viewAll']
          } as RouteMeta
        },
        {
          path: 'audit',
          name: 'AdminAudit',
          component: () => import('../views/admin/AuditView.vue'),
          meta: {
            title: 'Audit',
            permissions: ['reports.viewAll']
          } as RouteMeta
        },
        {
          path: 'analytics',
          name: 'AdminAnalytics',
          component: () => import('../views/admin/AnalyticsDashboard.vue'),
          meta: {
            title: 'Analytics Dashboard',
            permissions: ['reports.viewAll']
          } as RouteMeta
        },
        {
          path: 'adoption',
          redirect: '/admin/reports'
        },
        {
          path: 'subscription-plans',
          name: 'AdminSubscriptionPlans',
          component: () => import('../views/admin/SubscriptionPlansView.vue'),
          meta: {
            title: 'Subscription Plans',
            permissions: ['admin.subscription_plans.read']
          } as RouteMeta
        },
        {
          path: 'operations',
          name: 'AdminOperations',
          component: () => import('../views/admin/OperationsDashboardView.vue'),
          meta: {
            title: 'Operations Dashboard',
            permissions: ['admin.operations.read']
          } as RouteMeta
        },
        {
          path: 'conversations',
          name: 'AdminConversations',
          component: () => import('../views/admin/AdminConversationsView.vue'),
          meta: {
            title: 'Customer Conversations'
          } as RouteMeta
        },
        // Blog routes
        {
          path: 'manageBlogs',
          name: 'AdminBlogManagement',
          component: () => import('../views/admin/BlogManagementView.vue'),
          meta: {
            title: 'Blog Management'
          } as RouteMeta
        },
        {
          path: 'manageBlogs/new',
          name: 'AdminBlogNew',
          component: () => import('../views/admin/BlogEditorView.vue'),
          meta: {
            title: 'New Blog Post'
          } as RouteMeta
        },
        {
          path: 'manageBlogs/:id/edit',
          name: 'AdminBlogEdit',
          component: () => import('../views/admin/BlogEditorView.vue'),
          meta: {
            title: 'Edit Blog Post'
          } as RouteMeta
        },
        // Marketing routes
        {
          path: 'marketing',
          redirect: '/admin/marketing/campaigns'
        },
        {
          path: 'marketing/campaigns',
          name: 'AdminMarketingCampaigns',
          component: () => import('../views/admin/marketing/CampaignsView.vue'),
          meta: {
            title: 'Marketing Campaigns',
            permissions: ['admin.marketing.campaigns.read']
          } as RouteMeta
        },
        {
          path: 'marketing/recipients',
          name: 'AdminMarketingRecipients',
          component: () => import('../views/admin/marketing/RecipientsView.vue'),
          meta: {
            title: 'Recipients List',
            permissions: ['admin.marketing.recipients.read']
          } as RouteMeta
        },
        {
          path: 'marketing/categories',
          name: 'AdminMarketingCategories',
          component: () => import('../views/admin/marketing/CategoriesView.vue'),
          meta: {
            title: 'Marketing Categories',
            permissions: ['admin.marketing.campaigns.read']
          } as RouteMeta
        },
        {
          path: 'reviews',
          name: 'AdminReviews',
          component: () => import('../views/admin/AdminReviewsView.vue'),
          meta: {
            title: 'Reviews Management'
          } as RouteMeta
        }
      ]
    },

    // Shop Admin routes (Uses unified layout)
    {
      path: '/admin/shop',
      component: () => import('../views/admin/UnifiedAdminLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Admin'],
        hideNavigation: true
      } as RouteMeta,
      children: [
        {
          path: '',
          redirect: '/admin/shop/dashboard'
        },
        {
          path: 'dashboard',
          name: 'ZaveatoShopAdminDashboard',
          component: () => import('../views/admin/shop/DashboardView.vue'),
          meta: {
            title: 'Zaveato Shop Dashboard'
          } as RouteMeta
        },
        {
          path: 'shops',
          name: 'ZaveatoShopAdminShops',
          component: () => import('../views/admin/shop/ShopsView.vue'),
          meta: {
            title: 'Shops Management'
          } as RouteMeta
        },
        {
          path: 'shops/:id',
          name: 'ZaveatoShopAdminShopDetail',
          component: () => import('../views/admin/shop/ShopDetailView.vue'),
          meta: {
            title: 'Shop Details'
          } as RouteMeta
        },
        {
          path: 'products',
          name: 'ZaveatoShopAdminProducts',
          component: () => import('../views/admin/shop/ProductsView.vue'),
          meta: {
            title: 'Products Browser'
          } as RouteMeta
        },
        {
          // Slug regen tool sits under /admin/shop/* so the UnifiedAdminLayout
          // keeps the Shop nav active (it flips to the Services nav for any
          // route outside /admin/shop). Same permission gate as other Shop
          // admin surfaces; BE enforces [Authorize(Roles = "Admin")] separately.
          path: 'tools/slugs',
          name: 'AdminSlugTools',
          component: () => import('../views/admin/SlugToolsView.vue'),
          meta: {
            title: 'Slug Tools'
          } as RouteMeta
        },
        {
          path: 'categories',
          name: 'ZaveatoShopAdminCategories',
          component: () => import('../views/admin/shop/CategoriesView.vue'),
          meta: {
            title: 'Categories Management'
          } as RouteMeta
        },
        {
          path: 'platform-library',
          name: 'ZaveatoShopAdminPlatformLibrary',
          component: () => import('../views/admin/PlatformLibraryView.vue'),
          meta: {
            title: 'Product Variants & Options'
          } as RouteMeta
        },
        {
          path: 'orders',
          name: 'ZaveatoShopAdminOrders',
          component: () => import('../views/admin/shop/OrdersView.vue'),
          meta: {
            title: 'Orders Management'
          } as RouteMeta
        },
        {
          path: 'deliveries',
          name: 'ZaveatoShopAdminDeliveries',
          component: () => import('../views/admin/shop/DeliveriesDashboardView.vue'),
          meta: {
            title: 'Deliveries Dashboard'
          } as RouteMeta
        },
        {
          path: 'orders/:id',
          name: 'ZaveatoShopAdminOrderDetail',
          component: () => import('../views/admin/shop/OrderDetailView.vue'),
          meta: {
            title: 'Order Details'
          } as RouteMeta
        },
        {
          path: 'analytics',
          name: 'ZaveatoShopAdminAnalytics',
          component: () => import('../views/admin/shop/AnalyticsView.vue'),
          meta: {
            title: 'Analytics'
          } as RouteMeta
        },
        {
          path: 'settings',
          name: 'ZaveatoShopAdminSettings',
          component: () => import('../views/admin/shop/SettingsView.vue'),
          meta: {
            title: 'Shop Categories'
          } as RouteMeta
        },
        {
          path: 'custom-domains',
          name: 'ZaveatoShopAdminCustomDomains',
          component: () => import('../views/admin/shop/CustomDomainsView.vue'),
          meta: {
            title: 'Custom Domains'
          } as RouteMeta
        },
        {
          path: 'commissions',
          name: 'ZaveatoShopAdminCommissions',
          component: () => import('../views/admin/shop/CommissionsView.vue'),
          meta: {
            title: 'Commission Management'
          } as RouteMeta
        }
      ]
    },

    // ServiceUser routes
    {
      path: '/service-user',
      meta: {
        requiresAuth: true,
        roles: ['ServiceUser', 'Admin']
      } as RouteMeta,
      children: [
        {
          path: '',
          redirect: '/service-user/dashboard'
        },
        {
          path: 'dashboard',
          name: 'ServiceUserDashboard',
          component: () => import('../views/serviceuser/ServiceUserDashboardView.vue'),
          meta: {
            title: 'My Services',
            roles: ['ServiceUser', 'Admin']
          } as RouteMeta
        },
        {
          path: 'services/:id',
          name: 'ServiceUserServiceDetail',
          component: () => import('../views/serviceuser/ServiceDetailView.vue'),
          meta: {
            title: 'Service Detail',
            roles: ['ServiceUser', 'Admin']
          } as RouteMeta
        },
        {
          path: 'services/:id/edit',
          name: 'ServiceUserServiceEdit',
          component: () => import('../views/serviceuser/ServiceEditView.vue'),
          meta: {
            title: 'Edit Service',
            roles: ['ServiceUser', 'Admin']
          } as RouteMeta
        }
      ]
    },

    // Provider routes
    {
      path: '/provider',
      component: () => import('../views/provider/ProviderLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Provider', 'Admin']
      } as RouteMeta,
      children: [
        {
          path: '',
          name: 'ProviderHub',
          component: () => import('../views/provider/ProviderHubView.vue'),
          meta: {
            title: 'Provider Hub'
          } as RouteMeta
        },
        {
          path: 'appointments',
          name: 'ProviderAppointments',
          component: () => import('../views/provider/AppointmentsView.vue'),
          meta: {
            title: 'Appointments',
            permissions: ['appointments.view']
          } as RouteMeta
        },
        {
          path: 'appointments/live',
          name: 'ProviderLiveAppointments',
          component: () => import('../views/provider/LiveAppointmentsView.vue'),
          meta: {
            title: 'Live Appointments',
            permissions: ['appointments.view']
          } as RouteMeta
        },
        {
          path: 'schedule',
          name: 'ProviderSchedule',
          component: () => import('../views/provider/ScheduleView.vue'),
          meta: { 
            title: 'My Schedule' 
          } as RouteMeta
        },
        {
          path: 'services',
          name: 'ProviderServices',
          component: () => import('../views/provider/ServicesView.vue'),
          meta: {
            title: 'My Services',
            permissions: ['services.view']
          } as RouteMeta
        },
        {
          path: 'locations',
          name: 'ProviderLocations',
          component: () => import('../views/provider/LocationsView.vue'),
          meta: { 
            title: 'My Locations'
          } as RouteMeta
        },
        {
          path: 'documents',
          name: 'ProviderDocuments',
          component: () => import('../views/provider/DocumentsViewSimple.vue'),
          meta: { 
            title: 'My Documents' 
          } as RouteMeta
        },
        {
          path: 'specializations',
          name: 'ProviderSpecializations',
          component: () => import('../views/provider/SpecializationsView.vue'),
          meta: {
            title: 'Specializations & Certifications'
          } as RouteMeta
        },
        {
          path: 'payments',
          name: 'ProviderPayments',
          component: () => import('../views/provider/PaymentsView.vue'),
          meta: {
            title: 'Bank Details & Payments'
          } as RouteMeta
        },
        {
          path: 'profile',
          name: 'ProviderProfile',
          component: () => import('../views/provider/ProfileView.vue'),
          meta: {
            title: 'Provider Profile'
          } as RouteMeta
        },
        {
          path: 'settings',
          name: 'ProviderSettings',
          component: () => import('../views/provider/ProviderSettingsView.vue'),
          meta: {
            title: 'Provider Settings'
          } as RouteMeta
        },
        // Legacy — Receipt Printing is now a tab inside /provider/settings
        { path: 'receipt-printing', redirect: '/provider/settings' },
        {
          path: 'services/:id',
          name: 'ProviderServiceDetail',
          component: () => import('../views/provider/ServiceDetailView.vue'),
          meta: {
            title: 'Service Detail',
            permissions: ['services.view']
          } as RouteMeta
        },
        {
          path: 'services/:id/bookings',
          name: 'ProviderServiceBookings',
          component: () => import('../views/provider/ServiceBookingsView.vue'),
          meta: {
            title: 'Manage Bookings',
            permissions: ['appointments.view']
          } as RouteMeta
        },
        {
          path: 'services/:id/edit',
          name: 'ProviderServiceEdit',
          component: () => import('../views/provider/ServiceEditView.vue'),
          meta: {
            title: 'Edit Service',
            permissions: ['services.view']
          } as RouteMeta
        },
        {
          path: 'analytics',
          name: 'ProviderAnalytics',
          component: () => import('../views/provider/AnalyticsDashboard.vue'),
          meta: {
            title: 'Analytics Dashboard'
          } as RouteMeta
        },
        {
          path: 'subscription',
          name: 'ProviderSubscription',
          component: () => import('../views/provider/SubscriptionView.vue'),
          meta: {
            title: 'My Subscription'
          } as RouteMeta
        },
        {
          path: 'subscription/plans',
          name: 'ProviderPlans',
          component: () => import('../views/provider/PlansView.vue'),
          meta: {
            title: 'Subscription Plans'
          } as RouteMeta
        },
        {
          path: 'subscription/billing',
          name: 'ProviderBilling',
          component: () => import('../views/provider/BillingView.vue'),
          meta: {
            title: 'Billing History'
          } as RouteMeta
        },
        {
          path: 'commission/shop',
          name: 'ProviderShopCommission',
          component: () => import('../views/provider/ShopCommissionView.vue'),
          meta: {
            title: 'Shop Commission Rates'
          } as RouteMeta
        },
        {
          path: 'commission/service',
          name: 'ProviderServiceCommission',
          component: () => import('../views/provider/ServiceCommissionView.vue'),
          meta: {
            title: 'Service Commission Rates'
          } as RouteMeta
        },
        {
          path: 'commission/invoices',
          name: 'ProviderCommissionInvoices',
          component: () => import('../views/provider/CommissionInvoicesView.vue'),
          meta: {
            title: 'Commission Invoices'
          } as RouteMeta
        },
        {
          path: 'commission/payouts',
          name: 'ProviderCommissionPayouts',
          component: () => import('../views/provider/CommissionPayoutsView.vue'),
          meta: {
            title: 'Payouts'
          } as RouteMeta
        },
        // Personal customer activity (orders/appointments the provider made as a customer)
        {
          path: 'my-orders',
          name: 'ProviderMyOrders',
          component: () => import('../views/customer/CustomerOrdersView.vue'),
          meta: {
            title: 'My Orders'
          } as RouteMeta
        },
        {
          path: 'my-appointments',
          name: 'ProviderMyAppointments',
          component: () => import('../views/customer/MyAppointmentsView.vue'),
          meta: {
            title: 'My Appointments'
          } as RouteMeta
        },
        {
          path: 'my-messages',
          name: 'ProviderMyMessages',
          component: () => import('../views/storefront/MyConversationsView.vue'),
          meta: {
            title: 'My Messages'
          } as RouteMeta
        },
        {
          path: 'conversations',
          name: 'ProviderConversations',
          component: () => import('../views/provider/ProviderConversationsView.vue'),
          meta: {
            title: 'Customer Conversations'
          } as RouteMeta
        },
        {
          path: 'reviews',
          name: 'ProviderReviews',
          component: () => import('../views/provider/ProviderReviewsView.vue'),
          meta: {
            title: 'Reviews',
            roles: ['Provider']
          } as RouteMeta
        },
        // Wholesale Enquiries moved into the Zav Procurement module so the
        // sidebar chrome stays put on click. These `/provider/enquiries/*`
        // paths are kept as redirects only — old email deep-links and
        // browser bookmarks still land the user in the right place.
        { path: 'enquiries', redirect: '/procurement/enquiries' },
        { path: 'enquiries/:id', redirect: (to: any) => `/procurement/enquiries/${to.params.id}` },
        // In-app help guides (static content, no data). Linked from the
        // Provider Hub Procurement / Shelf tiles via a discreet
        // "How it works?" link that opens in a new tab.
        {
          path: 'help/invoicing',
          name: 'ProviderHelpInvoicing',
          component: () => import('../views/provider/help/ZavInvoiceHowItWorksView.vue'),
          meta: {
            title: 'ZavInvoice — How it works'
          } as RouteMeta
        },
        {
          path: 'help/shelf',
          name: 'ProviderHelpShelf',
          component: () => import('../views/provider/help/ZavShelfHowItWorksView.vue'),
          meta: {
            title: 'ZavShelf — How it works'
          } as RouteMeta
        }
      ]
    },

    // ============================================================================
    // Public Storefront Routes (Customer-facing Shop)
    // ============================================================================
    // Permanent QR code redirect routes (ID-based, never change)
    {
      path: '/s/:shopId',
      name: 'ShopQrRedirect',
      component: () => import('../views/storefront/ShopRedirectView.vue'),
      meta: { title: 'Redirecting...', requiresAuth: false } as RouteMeta
    },
    {
      path: '/sv/:serviceId',
      name: 'ServiceQrRedirect',
      component: () => import('../views/storefront/ServiceRedirectView.vue'),
      meta: { title: 'Redirecting...', requiresAuth: false } as RouteMeta
    },
    {
      path: '/shops',
      name: 'ShopsListing',
      component: () => import('../views/storefront/ShopsListView.vue'),
      meta: {
        title: 'Browse Shops',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      // Keyword landings share the HomeView template — same marketplace UX
      // the home page provides, with a per-slug LandingPageConfig (see
      // useLandingConfig) driving the H1 / meta title / meta description /
      // canonical / FAQ + Breadcrumb JSON-LD. Every URL exposes unique
      // content, so this is SEO-safe (not a doorway pattern). Adding a new
      // keyword URL = add its config entry + append its slug to this
      // pattern; no new .vue file, no component change.
      path: '/:slug(halal-meat|halal-groceries|halal-butchers|fresh-halal-delivery|rusholme-halal-meat|longsight-halal-meat|cheetham-hill-halal-meat|levenshulme-halal-meat|moss-side-halal-meat|didsbury-halal-meat)',
      name: 'HalalLanding',
      component: () => import('../views/public/HomeView.vue'),
      meta: {
        title: 'Halal Food & Groceries',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      // Manchester-scoped alias — backend SitemapController advertises these
      // URLs and Google has already crawled them, so they must resolve.
      // Renders the same HomeView; useSeo self-canonicals per pathname, so
      // /manchester/halal-meat and /halal-meat are BOTH indexable with
      // their own canonical (Google may dedup — that's fine, either wins).
      path: '/manchester/:slug(halal-meat|halal-groceries|halal-butchers|fresh-halal-delivery)',
      name: 'ManchesterHalalLanding',
      component: () => import('../views/public/HomeView.vue'),
      meta: {
        title: 'Halal Food & Groceries in Manchester',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      // Entry view picks BlogListView (canonical) or WhiteLabelBlogListView (WL)
      // based on isWhiteLabel — same URL, two audiences.
      path: '/blog',
      name: 'BlogList',
      component: () => import('../views/blog/BlogEntryView.vue'),
      meta: {
        title: 'Blog',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/blog/shops-and-food',
      name: 'BlogCategoryShopsFood',
      component: () => import('../views/blog/BlogCategoryView.vue'),
      meta: {
        title: 'Halal Shops & Food',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/blog/services',
      name: 'BlogCategoryServices',
      component: () => import('../views/blog/BlogCategoryView.vue'),
      meta: {
        title: 'Halal Services',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/blog/:slug',
      name: 'BlogPost',
      component: () => import('../views/blog/BlogPostEntryView.vue'),
      meta: {
        title: 'Blog Post',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/deals',
      name: 'DealsListing',
      component: () => import('../views/storefront/DealsView.vue'),
      meta: {
        title: 'Deals & Vouchers',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode',
      name: 'ShopHome',
      component: () => import('../views/storefront/ShopHomeView.vue'),
      meta: {
        title: 'Shop',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      // Public shareable flyer route. Shows the shop's uploaded custom flyer
      // (or the auto-generated one as a preview) with Download + Share CTAs
      // and rich OG meta so a shared link previews the flyer thumbnail on
      // WhatsApp / Twitter / LinkedIn.
      path: '/shop/:shopCode/flyer',
      name: 'ShopFlyerShare',
      component: () => import('../views/public/ShopFlyerView.vue'),
      meta: {
        title: 'Shop Flyer',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      // WL alias — a WL storefront serving `/` for its own shop still needs a
      // shareable flyer URL. Resolves the shopCode from the WL context (see
      // useShopDomain / useShopBrand) so `<host>/flyer` maps to the WL shop's
      // canonical flyer route.
      path: '/flyer',
      name: 'ShopFlyerShareWl',
      component: () => import('../views/public/ShopFlyerView.vue'),
      meta: {
        title: 'Shop Flyer',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/products',
      name: 'ShopProducts',
      component: () => import('../views/storefront/ShopProductsView.vue'),
      meta: {
        title: 'Products',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/about',
      name: 'ShopAbout',
      component: () => import('../views/storefront/WhiteLabelAboutView.vue'),
      meta: {
        title: 'About Us',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/product/:slug',
      name: 'ShopProductDetail',
      component: () => import('../views/storefront/ProductDetailView.vue'),
      meta: {
        title: 'Product',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/cart',
      name: 'ShopCart',
      component: () => import('../views/storefront/CartView.vue'),
      meta: {
        title: 'Shopping Cart',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/cart/:cartToken',
      name: 'SharedCart',
      component: () => import('../views/storefront/SharedCartView.vue'),
      meta: {
        title: 'Shared Cart',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/checkout',
      name: 'ShopCheckout',
      component: () => import('../views/storefront/CheckoutView.vue'),
      meta: {
        title: 'Checkout',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/order-confirmation',
      name: 'ShopOrderConfirmation',
      component: () => import('../views/storefront/OrderConfirmationView.vue'),
      meta: {
        title: 'Order Confirmed',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    // Order availability response (POD orders) - MUST be before /orders/:orderNumber to match correctly
    {
      path: '/shop/:shopCode/orders/availability/:token',
      name: 'OrderAvailabilityResponse',
      component: () => import('../views/storefront/OrderAvailabilityResponseView.vue'),
      meta: {
        title: 'Order Availability',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/shop/:shopCode/orders/:orderNumber',
      name: 'CustomerOrderView',
      component: () => import('../views/storefront/CustomerOrderView.vue'),
      meta: {
        title: 'Order Details',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    // Support plural "shops" URL from email links
    {
      path: '/shops/:shopCode/orders/:orderNumber',
      redirect: (to: any) => ({
        name: 'CustomerOrderView',
        params: { shopCode: to.params.shopCode, orderNumber: to.params.orderNumber },
        query: to.query
      })
    },

    // =========================================================================
    // CUSTOM DOMAIN STOREFRONT ROUTES
    // =========================================================================
    // These routes are used when the shop is accessed via subdomain or custom domain.
    // They provide clean URLs without /shop/:shopCode prefix.
    // Example: ibrahim.staging.zaveato.co.uk/products instead of /shop/igds/products
    // The shopCode is determined from the domain, not the URL.
    // =========================================================================
    {
      path: '/storefront',
      name: 'StorefrontHome',
      component: () => import('../views/storefront/ShopHomeView.vue'),
      meta: {
        title: 'Shop',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/products',
      name: 'StorefrontProducts',
      component: () => import('../views/storefront/ShopProductsView.vue'),
      meta: {
        title: 'Products',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/about',
      name: 'StorefrontAbout',
      component: () => import('../views/storefront/WhiteLabelAboutView.vue'),
      meta: {
        title: 'About Us',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/product/:slug',
      name: 'StorefrontProductDetail',
      component: () => import('../views/storefront/ProductDetailView.vue'),
      meta: {
        title: 'Product',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/cart',
      name: 'StorefrontCart',
      component: () => import('../views/storefront/CartView.vue'),
      meta: {
        title: 'Shopping Cart',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/checkout',
      name: 'StorefrontCheckout',
      component: () => import('../views/storefront/CheckoutView.vue'),
      meta: {
        title: 'Checkout',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/order-confirmation',
      name: 'StorefrontOrderConfirmation',
      component: () => import('../views/storefront/OrderConfirmationView.vue'),
      meta: {
        title: 'Order Confirmed',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },
    {
      path: '/storefront/orders/:orderNumber',
      name: 'StorefrontOrderView',
      component: () => import('../views/storefront/CustomerOrderView.vue'),
      meta: {
        title: 'Order Details',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true
      } as RouteMeta
    },

    // Shop routes (Provider E-commerce)
    {
      path: '/shop',
      component: () => import('../views/shop/ShopLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Provider', 'Admin', 'ShopStaff'],
        hideNavigation: true
      } as RouteMeta,
      children: [
        {
          path: '',
          redirect: { name: 'ZaveatoShopDashboard' }
        },
        {
          path: 'dashboard',
          name: 'ZaveatoShopDashboard',
          component: () => import('../views/shop/DashboardView.vue'),
          meta: {
            title: 'Shop Dashboard'
          } as RouteMeta
        },
        {
          path: 'setup',
          name: 'ZaveatoShopSetup',
          component: () => import('../views/shop/ShopSetupView.vue'),
          meta: {
            title: 'Shop Setup'
          } as RouteMeta
        },
        {
          path: 'settings',
          name: 'ZaveatoShopSettings',
          component: () => import('../views/shop/ShopSettingsView.vue'),
          meta: {
            title: 'Shop Settings'
          } as RouteMeta
        },
        // Wholesale Buyers now lives inside the Procurement module — keep this
        // URL working by redirecting so any bookmarks/deep-links don't 404.
        { path: 'wholesale-buyers', redirect: '/procurement/wholesale-buyers' },
        {
          path: 'products',
          name: 'ZaveatoShopProducts',
          component: () => import('../views/shop/ProductsView.vue'),
          meta: {
            title: 'Products'
          } as RouteMeta
        },
        {
          path: 'products/new',
          name: 'ZaveatoShopProductNew',
          component: () => import('../views/shop/ProductEditView.vue'),
          meta: {
            title: 'New Product'
          } as RouteMeta
        },
        {
          path: 'products/import',
          name: 'ZaveatoShopProductImport',
          component: () => import('../views/shop/ProductImportView.vue'),
          meta: {
            title: 'Import Products'
          } as RouteMeta
        },
        {
          path: 'products/imports',
          name: 'ZaveatoShopProductImportHistory',
          component: () => import('../views/shop/ProductImportHistoryView.vue'),
          meta: {
            title: 'Import History'
          } as RouteMeta
        },
        {
          path: 'products/exports',
          name: 'ZaveatoShopProductExports',
          component: () => import('../views/shop/ProductExportsView.vue'),
          meta: {
            title: 'Export Products'
          } as RouteMeta
        },
        {
          path: 'library',
          name: 'ZaveatoShopLibrary',
          component: () => import('../views/shop/LibraryManagementView.vue'),
          meta: {
            title: 'Variant & Option Library'
          } as RouteMeta
        },
        {
          path: 'products/:id',
          name: 'ZaveatoShopProductEdit',
          component: () => import('../views/shop/ProductEditView.vue'),
          meta: {
            title: 'Edit Product'
          } as RouteMeta
        },
        {
          path: 'categories',
          name: 'ZaveatoShopCategories',
          component: () => import('../views/shop/CategoriesView.vue'),
          meta: {
            title: 'Categories'
          } as RouteMeta
        },
        {
          path: 'extras',
          name: 'ZaveatoShopExtras',
          component: () => import('../views/shop/ProductExtrasView.vue'),
          meta: {
            title: 'Product Extras'
          } as RouteMeta
        },
        {
          path: 'gallery',
          name: 'ZaveatoShopGallery',
          component: () => import('../views/shop/ShopGalleryView.vue'),
          meta: {
            title: 'Image Gallery'
          } as RouteMeta
        },
        {
          path: 'availability',
          name: 'ZaveatoShopAvailability',
          component: () => import('../views/shop/ShopAvailabilityView.vue'),
          meta: {
            title: 'Availability'
          } as RouteMeta
        },
        {
          path: 'staff',
          name: 'ZaveatoShopStaff',
          component: () => import('../views/shop/ShopStaffView.vue'),
          meta: {
            title: 'Staff Management'
          } as RouteMeta
        },
        {
          path: 'orders',
          name: 'ZaveatoShopOrders',
          component: () => import('../views/shop/OrdersView.vue'),
          meta: {
            title: 'Orders'
          } as RouteMeta
        },
        {
          path: 'orders/live',
          name: 'ZaveatoShopLiveDashboard',
          component: () => import('../views/shop/OrdersLiveDashboard.vue'),
          meta: {
            title: 'Live Orders'
          } as RouteMeta
        },
        {
          path: 'orders/:id',
          name: 'ZaveatoShopOrderDetail',
          component: () => import('../views/shop/OrderDetailView.vue'),
          meta: {
            title: 'Order Details'
          } as RouteMeta
        },
        {
          path: 'stripe',
          name: 'ZaveatoShopStripe',
          component: () => import('../views/shop/ShopStripeView.vue'),
          meta: {
            title: 'Payment Setup'
          } as RouteMeta
        },
        {
          path: 'commission-rates',
          name: 'ZaveatoShopCommissionRates',
          component: () => import('../views/shop/ShopCommissionRatesView.vue'),
          meta: {
            title: 'Commission Rates'
          } as RouteMeta
        },
        {
          path: 'promo-codes',
          name: 'ZaveatoShopPromoCodes',
          component: () => import('../views/shop/PromoCodesView.vue'),
          meta: {
            title: 'Promo Codes'
          } as RouteMeta
        },
        {
          path: 'analytics',
          name: 'ZaveatoShopAnalytics',
          component: () => import('../views/shop/AnalyticsDashboardView.vue'),
          meta: {
            title: 'Analytics'
          } as RouteMeta
        },
        // Legacy Zav Shelf routes — redirect to top-level /shelflife/* for
        // backwards compatibility.
        { path: 'shelflife', redirect: '/shelflife' },
        { path: 'shelflife/items', redirect: '/shelflife/items' },
        { path: 'shelflife/items/add', redirect: '/shelflife/items/add' },
        { path: 'shelflife/items/:id', redirect: (to: any) => `/shelflife/items/${to.params.id}` },
        // Legacy Zav Invoice routes — redirect into /procurement/* for
        // backwards compatibility with bookmarks and existing links.
        { path: 'invoices', redirect: '/procurement' },
        { path: 'invoices/create', redirect: '/procurement/create' },
        { path: 'invoices/edit/:id', redirect: (to: any) => `/procurement/edit/${to.params.id}` },
        { path: 'invoices/buyers', redirect: '/procurement/buyers' },
        { path: 'invoices/suppliers', redirect: '/procurement/suppliers' },
        { path: 'invoices/suppliers/:supplierId', redirect: (to: any) => `/procurement/suppliers/${to.params.supplierId}` },
        { path: 'invoices/suppliers/:supplierId/invoices/new', redirect: (to: any) => `/procurement/suppliers/${to.params.supplierId}/invoices/new` },
        { path: 'invoices/:id', redirect: (to: any) => `/procurement/${to.params.id}` },
      ]
    },

    // ═══════════════════════════════════════════════════════════════════
    // Zav Shelf (own module — dedicated ShelfLifeLayout with back-to-hub)
    // ═══════════════════════════════════════════════════════════════════
    {
      path: '/shelflife',
      // Renders inside its own module layout (top brand bar + sidebar +
      // "Back to Provider Hub" link), mirroring the Shop module pattern.
      component: () => import('../views/shelflife/ShelfLifeLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Provider', 'Admin', 'ShopStaff'],
        hideNavigation: true
      } as RouteMeta,
      children: [
        {
          path: '',
          name: 'ShelfLifeDashboard',
          component: () => import('../views/provider/shelflife/ShelfLifeDashboard.vue'),
          props: { initialTab: 'items' },
          meta: { title: 'Stock Items', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'expiring',
          name: 'ShelfLifeExpiring',
          component: () => import('../views/provider/shelflife/ShelfLifeDashboard.vue'),
          props: { initialTab: 'expiring' },
          meta: { title: 'Expiry Tracker', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'expired',
          name: 'ShelfLifeExpired',
          component: () => import('../views/provider/shelflife/ShelfLifeDashboard.vue'),
          props: { initialTab: 'expired' },
          meta: { title: 'Expired Stock', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        // Legacy /shelflife/items → dashboard's items view; keep sub-item detail/add
        { path: 'items', redirect: '/shelflife' },
        {
          path: 'items/add',
          name: 'ShelfLifeAddItem',
          component: () => import('../views/provider/shelflife/AddStockItem.vue'),
          meta: { title: 'Add Stock Item', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'items/:id',
          name: 'ShelfLifeItemDetail',
          component: () => import('../views/provider/shelflife/StockItemDetail.vue'),
          meta: { title: 'Stock Item', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
      ]
    },

    // ═══════════════════════════════════════════════════════════════════
    // Zav Procurement (own module — dedicated ProcurementLayout with back-to-hub)
    // Each sidebar entry maps to its own child route; InvoiceDashboard reads
    // `initialTab` (via route meta / props) to decide which section to show.
    // The in-page tab strip has been removed in favour of these routes.
    // ═══════════════════════════════════════════════════════════════════
    {
      path: '/procurement',
      component: () => import('../views/procurement/ProcurementLayout.vue'),
      meta: {
        requiresAuth: true,
        roles: ['Provider', 'Admin', 'ShopStaff'],
        hideNavigation: true
      } as RouteMeta,
      children: [
        {
          path: '',
          name: 'ProcurementDashboard',
          component: () => import('../views/procurement/ProcurementDashboardView.vue'),
          meta: {
            title: 'Zav Procurement',
            roles: ['Provider', 'Admin', 'ShopStaff']
          } as RouteMeta
        },
        {
          path: 'invoices',
          name: 'ProcurementInvoices',
          component: () => import('../views/shop/invoices/InvoiceDashboard.vue'),
          props: { initialTab: 'invoices' },
          meta: { title: 'Invoices', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'buyers',
          name: 'ProcurementBuyers',
          component: () => import('../views/shop/invoices/InvoiceDashboard.vue'),
          props: { initialTab: 'buyers' },
          meta: { title: 'Buyers', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'invitations',
          name: 'ProcurementInvitations',
          component: () => import('../views/shop/invoices/InvoiceDashboard.vue'),
          props: { initialTab: 'invitations' },
          meta: { title: 'Invitations', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'statements',
          name: 'ProcurementStatements',
          component: () => import('../views/shop/invoices/InvoiceDashboard.vue'),
          props: { initialTab: 'statements' },
          meta: { title: 'Statements', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'suppliers',
          name: 'ProcurementSuppliers',
          component: () => import('../views/shop/invoices/InvoiceDashboard.vue'),
          props: { initialTab: 'suppliers' },
          meta: { title: 'Suppliers', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'create',
          name: 'InvoiceCreate',
          component: () => import('../views/shop/invoices/InvoiceCreate.vue'),
          meta: { title: 'Create Invoice', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'generate-from-orders',
          name: 'InvoiceGenerateFromOrders',
          component: () => import('../views/shop/invoices/GenerateFromOrdersView.vue'),
          meta: { title: 'Generate Invoice from Orders', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'orders',
          name: 'WholesaleOrders',
          component: () => import('../views/procurement/WholesaleOrdersView.vue'),
          meta: { title: 'Wholesale Orders', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'wholesale-buyers',
          name: 'ProcurementWholesaleBuyers',
          component: () => import('../views/shop/WholesaleBuyersView.vue'),
          meta: { title: 'Wholesale Buyers', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        // Wholesale Enquiries — mounted inside ProcurementLayout so the
        // sidebar chrome stays put when the provider clicks the sidebar
        // entry (same pattern as Wholesale Buyers / Wholesale Orders).
        // The provider-scoped legacy route `/provider/enquiries/*` still
        // exists as a redirect below so old email links keep working.
        {
          path: 'enquiries',
          name: 'ProcurementEnquiries',
          component: () => import('../views/provider/EnquiriesView.vue'),
          meta: { title: 'Wholesale Enquiries', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'enquiries/:id',
          name: 'ProcurementEnquiryDetail',
          component: () => import('../views/provider/EnquiryDetailView.vue'),
          meta: { title: 'Enquiry', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'credit-notes',
          name: 'CreditNotesList',
          component: () => import('../views/procurement/CreditNotesListView.vue'),
          meta: { title: 'Credit Notes', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'credit-notes/:id',
          name: 'CreditNoteDetail',
          component: () => import('../views/procurement/CreditNoteDetailView.vue'),
          meta: { title: 'Credit Note', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'credit-notes/:id/edit',
          name: 'CreditNoteEdit',
          component: () => import('../views/procurement/CreditNoteEditView.vue'),
          meta: { title: 'Edit Credit Note', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: ':invoiceId/credit-notes/new',
          name: 'CreditNoteCreate',
          component: () => import('../views/shop/invoices/CreditNoteCreate.vue'),
          meta: { title: 'New Credit Note', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'edit/:id',
          name: 'InvoiceEdit',
          component: () => import('../views/shop/invoices/InvoiceCreate.vue'),
          meta: { title: 'Edit Invoice', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'suppliers/:supplierId',
          name: 'SupplierDetail',
          component: () => import('../views/shop/invoices/SupplierDetailView.vue'),
          meta: { title: 'Supplier', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: 'suppliers/:supplierId/invoices/new',
          name: 'SupplierInvoiceCreate',
          component: () => import('../views/shop/invoices/SupplierInvoiceCreate.vue'),
          meta: { title: 'Upload Supplier Invoice', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
        {
          path: ':id',
          name: 'InvoiceDetail',
          component: () => import('../views/shop/invoices/InvoiceDetail.vue'),
          meta: { title: 'Invoice', roles: ['Provider', 'Admin', 'ShopStaff'] } as RouteMeta
        },
      ]
    },

    // Legacy `/invoicing/*` URLs — keep working by 301-style redirecting into
    // the new /procurement/* namespace. Deep links, bookmarks and legacy tab
    // query params (?tab=X) all get translated to the new route names.
    {
      path: '/invoicing',
      redirect: (to: any) => {
        const t = to.query.tab as string | undefined
        if (t && ['invoices', 'buyers', 'invitations', 'statements', 'suppliers'].includes(t)) {
          return { path: `/procurement/${t}` }
        }
        return { path: '/procurement' }
      }
    },
    {
      path: '/invoicing/:pathMatch(.*)*',
      redirect: (to: any) => ({ path: `/procurement/${(to.params.pathMatch as string[] | undefined)?.join('/') ?? ''}` })
    },

    // Appointment slot response (public - for customer to accept/reject proposed time)
    {
      path: '/appointments/slot-response/:token',
      name: 'AppointmentSlotResponse',
      component: () => import('../views/appointments/AppointmentSlotResponseView.vue'),
      meta: {
        title: 'Appointment Response',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },

    // Appointment routes
    {
      path: '/appointments',
      component: () => import('../views/appointments/AppointmentLayout.vue'),
      meta: {
        requiresAuth: true,
        permissions: ['appointments.view']
      } as RouteMeta,
      children: [
        {
          path: '',
          name: 'Appointments',
          component: () => import('../views/appointments/AppointmentsView.vue'),
          meta: {
            title: 'Appointments'
          } as RouteMeta
        },
        {
          path: 'book',
          name: 'BookAppointment',
          component: () => import('../views/appointments/BookingView.vue'),
          meta: {
            title: 'Book Appointment',
            permissions: ['appointments.create']
          } as RouteMeta
        },
        {
          path: ':id/edit',
          name: 'EditAppointment',
          component: () => import('../views/appointments/EditAppointmentView.vue'),
          meta: {
            title: 'Edit Appointment',
            permissions: ['appointments.edit']
          } as RouteMeta
        },
        {
          path: ':id',
          name: 'AppointmentDetails',
          component: () => import('../views/appointments/AppointmentDetailsView.vue'),
          meta: { 
            title: 'Appointment Details'
          } as RouteMeta
        }
      ]
    },

    // Public Services routes
    {
      path: '/services',
      name: 'Services',
      component: () => import('../views/public/ServiceListingView.vue'),
      meta: {
        title: 'Browse Services',
        requiresAuth: false
      } as RouteMeta
    },
    // Service category landing pages — static routes match before /services/:id
    ...[
      'building-home-extension', 'education-and-learning', 'legal-services',
      'technology', 'driving-instructor', 'financial-services', 'automotive-repair',
      'beauty-wellness', 'flooring', 'food-beverage', 'gas-heating', 'health-beauty',
      'healthcare', 'home-electrics', 'home-interior-design', 'home-removals',
      'home-services', 'immigration', 'kitchen-bathroom', 'mobile-repair',
      'mot-car-repair', 'plumbing', 'software-saas', 'wellness-beauty'
    ].map(slug => ({
      path: `/services/${slug}`,
      name: `ServiceCategory_${slug}`,
      component: () => import('../views/public/ServiceCategoryView.vue'),
      meta: { title: 'Services', requiresAuth: false } as RouteMeta
    })),

    // Contact Us — wrapped so WL custom domains see the shop's own contact
    // form (with shop info + form that emails the shop owner) while canonical
    // Zaveato keeps the marketplace support form. URL stays at /contact.
    {
      path: '/contact',
      name: 'Contact',
      component: () => import('../views/ContactEntryView.vue'),
      meta: {
        title: 'Contact Us',
        requiresAuth: false
      } as RouteMeta
    },
    // Delete Account
    {
      path: '/delete-account',
      name: 'DeleteAccount',
      component: () => import('../views/public/DeleteAccountView.vue'),
      meta: {
        title: 'Delete Account',
        requiresAuth: false
      } as RouteMeta
    },
    // FAQ — entry view picks the canonical Zaveato FAQ or the shop-scoped
    // WhiteLabelFaqView based on host (same URL on both).
    {
      path: '/faq',
      name: 'FAQ',
      component: () => import('../views/FaqEntryView.vue'),
      meta: {
        title: 'Frequently Asked Questions',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },
    // Sitemap
    {
      path: '/sitemap',
      name: 'Sitemap',
      component: () => import('../views/SitemapView.vue'),
      meta: {
        title: 'Sitemap',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },
    {
      path: '/services/:id',
      name: 'ServiceDetail',
      component: () => import('../views/public/ServiceDetailView.vue'),
      meta: {
        title: 'Service Details',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },
    {
      path: '/appointments/view/:id/:token',
      name: 'PublicAppointmentView',
      component: () => import('../views/public/PublicAppointmentView.vue'),
      meta: {
        title: 'Appointment Details',
        requiresAuth: false  // Public view accessible via QR code
      } as RouteMeta
    },
    {
      path: '/invoices/view/:token',
      name: 'InvoicePublicView',
      component: () => import('../views/public/InvoicePublicView.vue'),
      meta: {
        title: 'Invoice',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/credit-notes/view/:token',
      name: 'CreditNotePublicView',
      component: () => import('../views/public/CreditNotePublicView.vue'),
      meta: {
        title: 'Credit Note',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/invoices/invitation/:token',
      name: 'InvoiceInvitationResponse',
      component: () => import('../views/public/InvoiceInvitationResponse.vue'),
      meta: {
        title: 'Buyer Invitation',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/wholesale/accept',
      name: 'WholesaleAcceptInvite',
      component: () => import('../views/public/WholesaleAcceptInviteView.vue'),
      meta: {
        title: 'Accept Wholesale Invitation',
        requiresAuth: false
      } as RouteMeta
    },
    {
      // Wholesale-buyer portal shell. All buyer pages nest under this so they
      // share the left-sidebar nav (Dashboard / Suppliers / Invoices / Credit
      // Notes) instead of duplicating a top tab-strip on every page.
      path: '/wholesale',
      component: () => import('../views/wholesale/WholesaleBuyerLayout.vue'),
      meta: { requiresAuth: true } as RouteMeta,
      children: [
        {
          path: '',
          name: 'WholesaleBuyerDashboard',
          component: () => import('../views/wholesale/WholesaleBuyerDashboardView.vue'),
          meta: {
            title: 'Procurement Overview',
            requiresAuth: true
          } as RouteMeta
        },
        {
          // "my-suppliers" — was the pre-layout canonical URL. Kept as-is so
          // existing links + emails still resolve, just now nested under the
          // shared layout.
          path: 'my-suppliers',
          name: 'MyWholesaleShops',
          component: () => import('../views/wholesale/MyWholesaleShopsView.vue'),
          meta: {
            title: 'My Wholesale Suppliers',
            requiresAuth: true
          } as RouteMeta
        },
        {
          // Per-supplier hub — Overview / Orders / Invoices tabs. Shipment D.
          // Cards on MyWholesaleShopsView deep-link here so the buyer lands
          // on their relationship with that supplier first, not the storefront.
          path: 'my-suppliers/:shopId',
          name: 'MyWholesaleSupplierDetail',
          component: () => import('../views/wholesale/SupplierDetailView.vue'),
          meta: {
            title: 'Supplier',
            requiresAuth: true
          } as RouteMeta
        },
        {
          path: 'invoices',
          name: 'MyWholesaleInvoices',
          component: () => import('../views/wholesale/MyInvoicesView.vue'),
          meta: {
            title: 'My Invoices',
            requiresAuth: true
          } as RouteMeta
        },
        {
          path: 'invoices/:invoiceId',
          name: 'MyWholesaleInvoiceDetail',
          component: () => import('../views/wholesale/BuyerInvoiceDetailView.vue'),
          meta: {
            title: 'Invoice',
            requiresAuth: true
          } as RouteMeta
        },
        {
          path: 'credit-notes',
          name: 'MyWholesaleCreditNotes',
          component: () => import('../views/wholesale/MyCreditNotesView.vue'),
          meta: {
            title: 'My Credit Notes',
            requiresAuth: true
          } as RouteMeta
        }
      ]
    },
    {
      // Legacy — pre-rename buyer landing. Redirect straight to the new
      // canonical URL. Cheap forever-redirect; can drop after a few months.
      path: '/wholesale/my-shops',
      redirect: '/wholesale/my-suppliers'
    },

    // New Booking System Routes
    {
      path: '/services/:id/book',
      name: 'ServiceBooking',
      component: () => import('../views/ServiceBookingView.vue'),
      meta: {
        title: 'Book Service',
        requiresAuth: false,
        isBookingFlow: true
      } as RouteMeta
    },
    {
      path: '/booking/checkout',
      name: 'BookingCheckout',
      component: () => import('../views/BookingCheckoutView.vue'),
      meta: {
        title: 'Checkout',
        requiresAuth: false,
        isBookingFlow: true
      } as RouteMeta
    },
    {
      path: '/booking/confirmation',
      name: 'BookingConfirmation',
      component: () => import('../views/BookingConfirmationView.vue'),
      meta: {
        title: 'Slot Confirmed',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/booking/lookup',
      name: 'BookingLookup',
      component: () => import('../views/BookingLookupView.vue'),
      meta: {
        title: 'Find Your Booking',
        requiresAuth: false
      } as RouteMeta
    },
    {
      path: '/shop/order/lookup',
      name: 'OrderLookup',
      component: () => import('../views/OrderLookupView.vue'),
      meta: {
        title: 'Find Your Order',
        requiresAuth: false
      } as RouteMeta
    },
    {
      // Clean alias used on WL custom domains where /shop/* paths aren't a
      // good fit (the shopCode is implied by the host, not the URL).
      path: '/track-order',
      name: 'TrackOrder',
      component: () => import('../views/OrderLookupView.vue'),
      meta: {
        title: 'Find Your Order',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },

    // Guest Dashboard Routes (Email Verification Access)
    {
      path: '/guest/verify',
      name: 'GuestVerify',
      component: () => import('../views/guest/GuestVerifyView.vue'),
      meta: {
        title: 'Access Your Bookings & Orders',
        requiresAuth: false,
        hideNavigation: true
      } as RouteMeta
    },
    {
      path: '/guest/dashboard',
      name: 'GuestDashboard',
      component: () => import('../views/guest/GuestDashboardView.vue'),
      meta: {
        title: 'Your Bookings & Orders',
        requiresAuth: false, // Not standard auth, uses guest token
        requiresGuestAuth: true, // Custom flag for guest token auth
        hideNavigation: true
      } as RouteMeta
    },

    // Customer Appointments Dashboard (for logged-in users)
    {
      path: '/customer/appointments',
      name: 'MyAppointments',
      component: () => import('../views/customer/MyAppointmentsView.vue'),
      meta: {
        title: 'My Appointments',
        requiresAuth: true
      } as RouteMeta
    },

    // Customer conversations / complaints inbox
    {
      path: '/account/conversations',
      name: 'MyConversations',
      component: () => import('../views/storefront/MyConversationsView.vue'),
      meta: {
        title: 'My Messages',
        requiresAuth: true
      } as RouteMeta
    },

    // Legacy routes (keeping for backward compatibility)
    {
      path: '/appointments/lookup',
      redirect: '/booking/lookup'
    },

    // Legal routes - pages accessible to everyone
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditions',
      component: () => import('../views/legal/TermsAndConditionsView.vue'),
      meta: {
        title: 'Terms & Conditions',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: () => import('../views/legal/PrivacyPolicyView.vue'),
      meta: {
        title: 'Privacy Policy',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },
    {
      path: '/cookies-policy',
      name: 'CookiesPolicy',
      component: () => import('../views/legal/CookiesPolicyView.vue'),
      meta: {
        title: 'Cookies Policy',
        requiresAuth: false  // Explicitly mark as public route
      } as RouteMeta
    },

    // Providers routes
    {
      path: '/providers',
      name: 'Providers',
      component: () => import('../views/providers/ProvidersView.vue'),
      meta: {
        requiresAuth: true,
        title: 'Providers',
        permissions: ['providers.view']
      } as RouteMeta
    },

    // Error routes - Public pages
    {
      path: '/error',
      name: 'Error',
      component: () => import('../views/ErrorView.vue'),
      meta: {
        title: 'Error',
        hideNavigation: true,
        requiresAuth: false  // Error pages should be accessible to everyone
      } as RouteMeta
    },
    {
      path: '/404',
      name: 'NotFound',
      component: () => import('../views/errors/NotFoundView.vue'),
      meta: {
        title: 'Page Not Found',
        hideNavigation: true,
        requiresAuth: false  // Error pages should be accessible to everyone
      } as RouteMeta
    },
    {
      path: '/403',
      name: 'Forbidden',
      component: () => import('../views/errors/ForbiddenView.vue'),
      meta: {
        title: 'Access Denied',
        hideNavigation: true,
        requiresAuth: false  // Error pages should be accessible to everyone
      } as RouteMeta
    },
    {
      path: '/500',
      name: 'ServerError',
      component: () => import('../views/errors/ServerErrorView.vue'),
      meta: {
        title: 'Server Error',
        hideNavigation: true,
        requiresAuth: false  // Error pages should be accessible to everyone
      } as RouteMeta
    },

    // =========================================================================
    // CLEAN URL ROUTES FOR CUSTOM DOMAINS
    // These routes allow custom domains to use clean URLs like /checkout
    // instead of /shop/:shopCode/checkout. They must be BEFORE the catch-all.
    // The navigation guard will verify the domain and set up shop context.
    // =========================================================================
    {
      path: '/products',
      name: 'CleanProducts',
      component: () => import('../views/storefront/ShopProductsView.vue'),
      meta: {
        title: 'Products',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    {
      path: '/product/:slug',
      name: 'CleanProductDetail',
      component: () => import('../views/storefront/ProductDetailView.vue'),
      meta: {
        title: 'Product',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    // Custom domain: /products/<category-slug>. Mounts the same products view
    // as /products so the WL engine dispatcher can pick a pack-owned page
    // (board-engine reads the slug from route.params to pre-select the tab).
    // Without this route, footer links like /products/meat 404 at the router
    // level before the dispatcher ever runs.
    {
      path: '/products/:slug',
      name: 'CleanProductsCategory',
      component: () => import('../views/storefront/ShopProductsView.vue'),
      meta: {
        title: 'Products',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    // Hierarchical category URL — /products/{parent}/{child}, e.g.
    // /products/meat/chicken. Same view; the pack Products.vue reads the
    // deepest slug from route.params.child as the active filter, and the
    // cascading catMatches ensures the child products render inside the
    // parent when only /products/{parent} is loaded. Google gets the URL
    // hierarchy signal (parent → child) for free.
    {
      path: '/products/:parent/:child',
      name: 'CleanProductsCategoryChild',
      component: () => import('../views/storefront/ShopProductsView.vue'),
      meta: {
        title: 'Products',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    {
      path: '/cart',
      name: 'CleanCart',
      component: () => import('../views/storefront/CartView.vue'),
      meta: {
        title: 'Shopping Cart',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    {
      path: '/checkout',
      name: 'CleanCheckout',
      component: () => import('../views/storefront/CheckoutView.vue'),
      meta: {
        title: 'Checkout',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    {
      path: '/order-confirmation',
      name: 'CleanOrderConfirmation',
      component: () => import('../views/storefront/OrderConfirmationView.vue'),
      meta: {
        title: 'Order Confirmed',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },
    // Customer Account (WL pack surface). The main WL Chrome links point at
    // /account, /account/orders, /account/profile — without these router
    // entries the paths 404 at the router level before the WL dispatcher
    // ever gets to render Account.vue. On WL hosts <WlEngineDispatcher/>
    // in App.vue takes over rendering, so the mounted component below is
    // effectively bypassed; it still resolves the path so navigation works.
    // On canonical Zaveato hosts we fall back to CustomerOrdersView for the
    // orders sub-path and let /account (bare) + /account/profile mount the
    // same view — canonical customers reach these via `/my-orders` today so
    // this stays a WL-scoped surface without breaking canonical.
    {
      path: '/account',
      name: 'CleanAccount',
      component: () => import('../views/customer/CustomerOrdersView.vue'),
      meta: {
        title: 'Account',
        requiresAuth: true,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true,
      } as RouteMeta
    },
    {
      path: '/account/orders',
      name: 'CleanAccountOrders',
      component: () => import('../views/customer/CustomerOrdersView.vue'),
      meta: {
        title: 'My Orders',
        requiresAuth: true,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true,
      } as RouteMeta
    },
    {
      path: '/account/profile',
      name: 'CleanAccountProfile',
      component: () => import('../views/customer/CustomerOrdersView.vue'),
      meta: {
        title: 'Profile',
        requiresAuth: true,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true,
      } as RouteMeta
    },
    {
      path: '/orders/:orderNumber',
      name: 'CleanOrderView',
      component: () => import('../views/storefront/CustomerOrderView.vue'),
      meta: {
        title: 'Order Details',
        requiresAuth: false,
        hideNavigation: true,
        isStorefrontRoute: true,
        isCleanUrl: true
      } as RouteMeta
    },

    // Catch all route - must be last
    {
      path: '/:pathMatch(.*)*',
      redirect: '/404'
    }
  ]
})

// Global navigation guards
router.beforeEach(async (to: any, _from: any, next: any) => {
  try {
    const authStore = useAuthStore()
    const appStore = useAppStore()

    // Start loading indicator
    appStore.startLoading('Navigating...')

    // =========================================================================
    // CUSTOM DOMAIN DETECTION & URL REWRITING
    // =========================================================================
    // When the app is loaded on a custom subdomain (e.g., mybusiness.staging.zaveato.co.uk)
    // or a custom domain (e.g., mybusiness.com), rewrite URLs to use clean paths.
    // Example: / becomes /storefront, /products becomes /storefront/products
    // The shopCode is determined from the domain, not the URL.
    // =========================================================================
    const { isCustomDomain } = useShopDomain()

    if (isCustomDomain.value) {
      const brandStoreLog = useBrandStore()
      console.log('🌐 Custom domain navigation:', { path: to.path, resolvedShop: brandStoreLog.brand?.shopCode })

      // Skip shop resolution for error pages to prevent infinite loops
      if (to.path === '/404' || to.path === '/500' || to.name === 'NotFound') {
        console.log('⚠️ Error page on custom domain, skipping shop resolution')
        appStore.stopLoading()
        return next()
      }

      // Shop resolution is the brand store's job (single source of truth).
      // We wait for its bootstrap to finish, then read `resolutionState` to
      // decide 404 vs continue. This replaces a duplicate resolveShop() API
      // call that used to race with the brand store and hard-redirect to
      // /404 on any transient failure — collapsing the WL site's SEO.
      const brandStore = useBrandStore()
      await brandStore.bootstrap()

      if (brandStore.resolutionState === 'unknown-host') {
        // Backend explicitly returned 404 for this hostname — genuinely not
        // one of our shops. Safe to 404.
        console.warn('❌ Hostname not registered to any shop — redirecting to /404')
        appStore.stopLoading()
        return next({ path: '/404', replace: true })
      }

      if (brandStore.resolutionState === 'error') {
        // Transient failure. DO NOT 404 — the raw HTML from the server
        // already has correct WL meta (favicon, OG, title) baked in by
        // WlIndexHtmlRewriter, and the brand store will retry. The SPA
        // continues rendering; ShopHomeView / About handle missing brand
        // gracefully. This is the branch that fixes the SEO catastrophe.
        console.warn('⚠️ Transient shop resolution error — continuing with server-rendered chrome')
      } else if (brandStore.brand) {
        console.log(`🏪 Shop resolved: ${brandStore.brand.shopName} (${brandStore.brand.shopCode})`)
      }

      // Clean URL routes (like /checkout, /cart) are defined at the router
      // root and marked with isCleanUrl: true. The Home and About routes
      // are now wrapper components that swap to the WL storefront
      // automatically — no redirect needed, URLs stay at / and /about.
      // Shop context is available via useStorefrontShop() composable.

      // Any legacy /shop/:shopCode/* URL hit on a WL host gets rewritten to
      // its clean equivalent (covers external bookmarks and any in-app links
      // we didn't update yet). This must run BEFORE the title-setting code
      // so the wrong page never paints.
      if (typeof to.path === 'string' && to.path.startsWith('/shop/')) {
        const clean = rewriteShopPathToClean(to.path)
        if (clean !== null) {
          appStore.stopLoading()
          return next({ path: clean, query: to.query, hash: to.hash, replace: true })
        }
      }

      // For clean URL routes, just continue - shop context is already set
      if (to.meta?.isCleanUrl) {
        console.log('✅ Clean URL route, shop context available:', brandStoreLog.brand?.shopCode)
      }
    }

    // Set page title (don't prefix "Home -" for homepage, just show app name).
    // On a WL custom domain the suffix becomes the SHOP's name — otherwise
    // every browser tab on a verified WL host would still read "… - Zaveato",
    // breaking the brand promise (especially on Cart / Checkout / Payment /
    // OrderConfirmation, where the title is the most prominent Zaveato leak).
    const { brand } = useBranding()
    const { brand: shopBrand, isWhiteLabel } = useShopBrand()
    const brandName = isWhiteLabel.value && shopBrand.value?.shopName
      ? shopBrand.value.shopName
      : brand.value.name
    const pageTitle = to.meta?.title
    const title = (pageTitle && pageTitle !== 'Home')
      ? `${pageTitle} - ${brandName}`
      : brandName
    document.title = title

    // For public routes, skip all auth checks - they can be accessed by anyone
    // A route is public if requiresAuth is explicitly false OR if neither requiresAuth nor requiresGuest is set
    const isPublicRoute = to.meta?.requiresAuth === false ||
                          (to.meta?.requiresAuth === undefined && to.meta?.requiresGuest === undefined)

    console.log('🚦 Router Guard:', {
      path: to.path,
      requiresAuth: to.meta?.requiresAuth,
      requiresGuest: to.meta?.requiresGuest,
      isPublicRoute: isPublicRoute,
      isAuthenticated: authStore.isAuthenticated
    })

    if (isPublicRoute) {
      console.log('✅ Public route - allowing access')
      // Initialize auth silently in background if tokens exist (don't await or block navigation)
      if (!authStore.accessToken && localStorage.getItem('accessToken')) {
        // Do this async without blocking navigation to public routes
        Promise.resolve().then(() => {
          authStore.initializeAuth()
        })
      }

      // Check if navigating to guest verify page while already authenticated
      if (to.name === 'GuestVerify') {
        const guestStore = useGuestStore()
        if (guestStore.isGuestAuthenticated) {
          console.log('✅ Guest already authenticated, redirecting to dashboard')
          appStore.stopLoading()
          return next({ name: 'GuestDashboard', replace: true })
        }
      }

      // Check if route requires guest auth (email verification)
      if (to.meta?.requiresGuestAuth) {
        const guestStore = useGuestStore()
        if (!guestStore.isGuestAuthenticated) {
          console.log('🚫 Guest auth required but not authenticated, redirecting to verify')
          appStore.stopLoading()
          return next({ name: 'GuestVerify' })
        }
        console.log('✅ Guest authenticated - allowing access to guest dashboard')
      }

      // Public routes can be accessed by anyone (authenticated or not)
      appStore.stopLoading()
      return next()
    }

    // For protected routes, initialize auth state from localStorage if tokens exist
    if (!authStore.accessToken && localStorage.getItem('accessToken')) {
      console.log('🔐 Router: Initializing auth from localStorage')
      authStore.initializeAuth()
    }

    // Check if route requires authentication
    if (to.meta?.requiresAuth) {
      if (!authStore.isAuthenticated) {
      // Check if we have stored tokens before trying refresh
      const hasStoredToken = localStorage.getItem('accessToken')
      if (hasStoredToken) {
        // Initialize auth first if we have stored tokens
        try {
          authStore.initializeAuth()
          // If still not authenticated after initialization, check if token is expired
          if (!authStore.isAuthenticated) {
            // Only refresh if token is expired or near expiry
            if (authStore.isTokenExpired || authStore.isTokenNearExpiry) {
              console.log('🔄 Token expired/near expiry, attempting refresh in router guard')
              const refreshSuccess = await authStore.refreshTokens()
              if (!refreshSuccess) {
                appStore.stopLoading()
                return next({
                  name: 'Login',
                  query: { redirect: to.fullPath }
                })
              }
            } else {
              // Token is still valid but user is not authenticated, something is wrong
              console.warn('⚠️ Token is valid but user is not authenticated, clearing auth data')
              authStore.clearAuthData()
              appStore.stopLoading()
              return next({
                name: 'Login',
                query: { redirect: to.fullPath }
              })
            }
          }
        } catch (error: unknown) {
          console.error('Auth initialization error:', error)
          appStore.stopLoading()
          return next({
            name: 'Login',
            query: { redirect: to.fullPath }
          })
        }
      } else {
        // No stored tokens, redirect to login
        appStore.stopLoading()
        return next({
          name: 'Login',
          query: { redirect: to.fullPath }
        })
      }
    } else {
      // User is authenticated — if token is expired or near expiry, refresh now before
      // the component mounts and fires API calls, so they always have a valid token.
      if (authStore.isTokenExpired && !authStore.isRefreshing) {
        console.log('🔄 Token expired, refreshing before navigation completes')
        const ok = await authStore.refreshTokens(true)
        if (!ok) {
          appStore.stopLoading()
          return next({ name: 'Login', query: { redirect: to.fullPath } })
        }
      } else if (authStore.isTokenNearExpiry && !authStore.isRefreshing) {
        console.log('🔄 Token near expiry, refreshing before navigation completes')
        await authStore.refreshTokens()
      }
    }

    // Check role requirements
    if (to.meta.roles && (to.meta.roles as string[]).length > 0) {
      const hasRequiredRole = (to.meta.roles as string[]).some((role: string) => authStore.hasRole(role as 'Admin' | 'Provider' | 'Client' | 'Staff' | 'ServiceUser' | 'ShopStaff'))
      if (!hasRequiredRole) {
        appStore.stopLoading()
        return next({ name: 'Forbidden' })
      }
    }

    // Provider users: always redirect /dashboard to Provider Hub
    if (authStore.hasRole('Provider') && !authStore.hasRole('Admin')) {
      if (to.path === '/dashboard') {
        appStore.stopLoading()
        return next('/provider')
      }
    }

    // ShopStaff users: redirect from customer dashboard and restrict shop routes
    if (authStore.hasRole('ShopStaff') &&
        !authStore.hasRole('Provider') &&
        !authStore.hasRole('Admin')) {
      // Redirect customer dashboard to shop dashboard
      if (to.path === '/dashboard') {
        appStore.stopLoading()
        return next('/shop/dashboard')
      }

      // Restrict shop routes to allowed paths only
      if (to.path.startsWith('/shop')) {
        const allowedPaths = ['/shop/orders', '/shop/dashboard']
        const isAllowed = allowedPaths.some(path => to.path.startsWith(path))
        if (!isAllowed) {
          appStore.stopLoading()
          return next('/shop/dashboard')
        }
      }
    }

    // Check permission requirements
    if (to.meta.permissions && (to.meta.permissions as string[]).length > 0) {
      // Admin users have access to everything - bypass all permission checks
      const isAdmin = authStore.hasRole('Admin')

      if (!isAdmin) {
        const hasRequiredPermission = (to.meta.permissions as string[]).some((permission: string) =>
          authStore.hasPermission(permission)
        )

        // Special handling for Provider role - allow access to appointments and services
        const isProviderAccessingOwnResources = authStore.hasRole('Provider') &&
          (to.meta.permissions as string[]).some((permission: string) =>
            permission === 'appointments.view' || permission === 'services.view'
          )

        // Special handling for ServiceUser role - allow access to appointments and services for services they manage
        const isServiceUserAccessingOwnResources = authStore.hasRole('ServiceUser') &&
          (to.meta.permissions as string[]).some((permission: string) =>
            permission === 'appointments.view' || permission === 'services.view'
          )

        if (!hasRequiredPermission && !isProviderAccessingOwnResources && !isServiceUserAccessingOwnResources) {
          appStore.stopLoading()
          return next({ name: 'Forbidden' })
        }
      }
    }
  }

  // Check if route requires guest (unauthenticated) user
  if (to.meta?.requiresGuest && authStore.isAuthenticated) {
    appStore.stopLoading()
    // Redirect based on user role
    if (authStore.hasRole('Admin')) {
      return next({ name: 'AdminDashboard' })
    } else if (authStore.hasRole('Provider')) {
      return next({ path: '/provider/services' })
    } else if (authStore.hasRole('ServiceUser')) {
      return next({ path: '/service-user/dashboard' })
    }
    return next({ name: 'Dashboard' })
  }


  next()
  } catch (error) {
    console.error('Router navigation error:', error)
    // If there's an error in auth checks, allow navigation but log the issue
    next()
  }
})

router.afterEach((to: any, _from: any) => {
  try {
    const appStore = useAppStore()
    const { brand } = useBranding()
    const { brand: shopBrand, isWhiteLabel } = useShopBrand()

    // Stop loading indicator
    appStore.stopLoading()

    // Track page view with Google Analytics. Brand name follows the same WL
    // rule as document.title so analytics rows match what users actually see.
    const brandName = isWhiteLabel.value && shopBrand.value?.shopName
      ? shopBrand.value.shopName
      : brand.value.name
    const pageTitle = to.meta?.title ? `${to.meta.title} - ${brandName}` : brandName
    googleAnalytics.trackPageView(to.fullPath, pageTitle)

    // Note: Scroll behavior is handled by the scrollBehavior option in createRouter
  } catch (error) {
    console.error('Router after navigation error:', error)
  }
})

// Handle navigation errors - especially chunk loading failures after deployment
router.onError((error: Error) => {
  try {
    const appStore = useAppStore()
    console.error('Router error:', error)
    appStore.stopLoading()

    // Detect chunk loading failures (happens after new deployment when user has cached old index.html)
    const isChunkLoadError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      error.message?.includes('Unable to preload CSS') ||
      error.name === 'ChunkLoadError'

    if (isChunkLoadError) {
      console.warn('🔄 Chunk loading failed - a new version may be available. Refreshing...')

      // Check if we've already tried refreshing to prevent infinite loops
      const lastRefresh = sessionStorage.getItem('chunk_error_refresh')
      const now = Date.now()

      if (!lastRefresh || (now - parseInt(lastRefresh)) > 10000) {
        // Only auto-refresh if we haven't refreshed in the last 10 seconds
        sessionStorage.setItem('chunk_error_refresh', now.toString())

        // Show a brief message before refreshing
        appStore.showGlobalError(
          'A new version of the app is available. The page will refresh automatically.',
          'Update Available',
          'Router'
        )

        // Refresh after a short delay to show the message
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        // We've already tried refreshing recently - show manual refresh prompt
        appStore.showGlobalError(
          'Please refresh the page to load the latest version of the app. If the problem persists, clear your browser cache.',
          'Update Required',
          'Router'
        )
      }
    }
  } catch (err) {
    console.error('Router error handler failed:', err)
  }
})

export default router